import { useState, useEffect } from "react";
import { LegacyCard, ResourceList, ResourceItem, Text } from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { resendEmail, fetchById } from "services/email.service";
import PrettyDate from "components/format/PrettyDate";
import Loading from "components/loading/Loading";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";

interface ConfirmModalProps {
  id: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ id }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId, customerId, orderId } = useParams();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailEntity, setEmailEntity] = useState<any>([]);
  const [isFetchLoading, setIsFetchLoading] = useState<boolean>(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsFetchLoading(true);
        const data = await fetchById(companyId, id);
        setEmailEntity([
          {
            id: data.id,
            email: data.emailTo,
            subject: data.subject,
            sentAt: data.sentAt,
          },
        ]);
        setTimeout(() => {
          setIsFetchLoading(false);
        }, 300);
      } catch (e) {
        setIsFetchLoading(false);
      }
    }
    fetchData();
  }, [companyId, id]);

  const handleToSendRequest = async () => {
    try {
      setIsLoading(true);
      await resendEmail(companyId, id);
      if (customerId && companyId) {
        dispatch(fetchCustomer({ customerId, companyId: companyId! }));
      }
      if (orderId && companyId) {
        dispatch(fetchOrderDetails({ companyId, orderId }));
      }
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  if (isFetchLoading) {
    return <Loading />;
  }

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("Are_you_sure_you_want_to_resend_email")}
      </h1>

      <div style={{ margin: "10px 0" }}>
        <LegacyCard>
          <ResourceList
            resourceName={{ singular: "email", plural: "emails" }}
            items={emailEntity}
            renderItem={(item) => {
              const { id, email, subject, sentAt } = item;

              return (
                <ResourceItem
                  id={id}
                  accessibilityLabel={`View details for ${email}`}
                  onClick={() => {}}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {email}
                  </Text>
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" as="h3">
                      {subject}
                    </Text>
                    <PrettyDate dateString={sentAt} showTime={true} />
                  </InlineStack>
                </ResourceItem>
              );
            }}
          />
        </LegacyCard>
      </div>

      <InlineStack align="space-between">
        <Button onClick={handleToSendRequest} loading={isLoading}>
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
